import { useState, useCallback } from 'react';
import { exportService } from '../services/exportService';
import { useAppContext } from '../context/AppContext';
import type { ExportOptions } from '../types';

export interface ExportStatus {
  isExporting: boolean;
  progress: number;
  error: string | null;
  success: boolean;
}

export const useExport = () => {
  const { state } = useAppContext();
  const [status, setStatus] = useState<ExportStatus>({
    isExporting: false,
    progress: 0,
    error: null,
    success: false,
  });

  const resetStatus = useCallback(() => {
    setStatus({
      isExporting: false,
      progress: 0,
      error: null,
      success: false,
    });
  }, []);

  const exportHTML = useCallback(async (options?: Partial<ExportOptions>) => {
    try {
      setStatus({ isExporting: true, progress: 0, error: null, success: false });
      
      const exportOptions: ExportOptions = {
        filename: options?.filename,
        includeCSS: options?.includeCSS ?? true,
        customCSS: options?.customCSS,
        ...options,
      };

      // Update progress
      setStatus(prev => ({ ...prev, progress: 50 }));
      
      await exportService.exportHTML(state.document.content, exportOptions);
      
      setStatus({ isExporting: false, progress: 100, error: null, success: true });
      
      // Reset success status after 3 seconds
      setTimeout(resetStatus, 3000);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed';
      setStatus({ isExporting: false, progress: 0, error: errorMessage, success: false });
    }
  }, [state.document.content, resetStatus]);

  const exportMarkdown = useCallback(async (options?: Partial<ExportOptions>) => {
    try {
      setStatus({ isExporting: true, progress: 0, error: null, success: false });
      
      const exportOptions: ExportOptions = {
        filename: options?.filename,
        includeCSS: false, // Not applicable for markdown
        ...options,
      };

      // Update progress
      setStatus(prev => ({ ...prev, progress: 50 }));
      
      await exportService.exportMarkdown(state.document.content, exportOptions);
      
      setStatus({ isExporting: false, progress: 100, error: null, success: true });
      
      // Reset success status after 3 seconds
      setTimeout(resetStatus, 3000);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed';
      setStatus({ isExporting: false, progress: 0, error: errorMessage, success: false });
    }
  }, [state.document.content, resetStatus]);

  const exportPDF = useCallback(async (_options?: Partial<ExportOptions>) => {
    try {
      setStatus({ isExporting: true, progress: 0, error: null, success: false });
      
      // Update progress
      setStatus(prev => ({ ...prev, progress: 30 }));
      
      // Prepare the document for print
      // Add print-content class to the preview panel for better PDF styling
      const previewElement = document.querySelector('[data-preview-panel]');
      if (previewElement) {
        previewElement.classList.add('print-content');
      }
      
      // Update progress
      setStatus(prev => ({ ...prev, progress: 70 }));
      
      // Small delay to ensure styles are applied
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Trigger browser print dialog
      window.print();
      
      // Cleanup after print dialog
      setTimeout(() => {
        if (previewElement) {
          previewElement.classList.remove('print-content');
        }
      }, 1000);
      
      setStatus({ isExporting: false, progress: 100, error: null, success: true });
      
      // Reset success status after 3 seconds
      setTimeout(resetStatus, 3000);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'PDF export failed';
      setStatus({ isExporting: false, progress: 0, error: errorMessage, success: false });
      
      // Ensure cleanup on error
      const previewElement = document.querySelector('[data-preview-panel]');
      if (previewElement) {
        previewElement.classList.remove('print-content');
      }
    }
  }, [resetStatus]);

  return {
    status,
    exportHTML,
    exportMarkdown,
    exportPDF,
    resetStatus,
  };
};